import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

import { SITE_KEYWORDS, SITE_NAME, SITE_URL } from "@/constants/site";
import {
  HREFLANG,
  HTML_LANG,
  getLangFromPathname,
  OG_LOCALE,
  stripLocalePrefix,
  SUPPORTED_LANGUAGES,
} from "@/navigations/locale";
import {
  DEFAULT_ROBOTS,
  OG_IMAGE_URL,
  absoluteUrl,
  graphJsonLd,
  type JsonLd,
} from "@/utils/seo";

interface SeoProps {
  title: string;
  description: string;
  path?: string;
  noindex?: boolean;
  jsonLd?: JsonLd | JsonLd[];
}

export default function Seo({ title, description, path, noindex = false, jsonLd }: SeoProps) {
  const location = useLocation();
  const lang = getLangFromPathname(location.pathname);
  const basePath = path ?? stripLocalePrefix(location.pathname);
  const url = absoluteUrl(basePath, lang);
  const locale = OG_LOCALE[lang];
  const extras = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const structured = graphJsonLd([
    {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: title,
      description,
      inLanguage: HTML_LANG[lang],
      isPartOf: { "@id": `${SITE_URL}/#website` },
    },
    ...extras,
  ]);
  const robots = noindex ? "noindex, nofollow" : DEFAULT_ROBOTS;

  return (
    <Helmet prioritizeSeoTags>
      <html lang={HTML_LANG[lang]} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={SITE_KEYWORDS} />
      <meta name="author" content={SITE_NAME} />
      <meta name="geo.region" content="CA-BC" />
      <meta name="geo.placename" content="Vancouver" />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="bingbot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="theme-color" content="#0c4a6e" />
      <link rel="canonical" href={url} />
      <link rel="image_src" href={OG_IMAGE_URL} />
      {SUPPORTED_LANGUAGES.map((item) => (
        <link
          key={item}
          rel="alternate"
          hrefLang={HREFLANG[item]}
          href={absoluteUrl(basePath, item)}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={absoluteUrl(basePath)} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content="en_CA" />
      <meta property="og:locale:alternate" content="zh_CN" />
      <meta property="og:locale:alternate" content="fr_CA" />
      <meta property="og:image" content={OG_IMAGE_URL} />
      <meta property="og:image:secure_url" content={OG_IMAGE_URL} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:alt" content={`${SITE_NAME} water-quality monitoring robot`} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE_URL} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} water-quality monitoring preview`} />

      <script type="application/ld+json">{JSON.stringify(structured)}</script>
    </Helmet>
  );
}
